import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  guestCorridorProfileHref,
  guestExpertProfileHref,
} from "@/pages/discover/discoverGuest.const";
import { ecosystemHeadProfileHref } from "@/pages/ecosystem-heads/ecosystemHeads.const";
import {
  expertCompanyProfileHref,
  expertNavState,
  expertProfileHref,
} from "@/pages/experts/experts.const";
import SectorPromoModal, {
  type SectorPromoModalData,
} from "@/pages/sector-ecosystem/components/SectorPromoModal";
import trpc from "@/shared/utils/trpc";

type PublicSectorPromo = {
  id: string;
  imageUrl: string;
  targetType: "CORRIDOR" | "EXPERT";
  targetUserId: string;
  industry: string | null;
  companyId: string | null;
  ctaLabel: string;
};

function rotateKey(slug: string) {
  return `sectorPromoRotate:${slug}`;
}

function pickRotatedPromo(slug: string, promos: PublicSectorPromo[]): PublicSectorPromo {
  if (promos.length === 1) return promos[0];

  let index = 0;
  try {
    const raw = sessionStorage.getItem(rotateKey(slug));
    const parsed = raw ? Number(raw) : 0;
    index = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
  } catch {
    index = 0;
  }

  const selected = promos[index % promos.length];
  try {
    sessionStorage.setItem(rotateKey(slug), String((index + 1) % promos.length));
  } catch {
    /* ignore quota / private mode */
  }
  return selected;
}

function resolveJoinHref(
  promo: PublicSectorPromo,
  isAuthenticated: boolean,
): string | null {
  const industry = promo.industry?.trim();

  if (promo.targetType === "CORRIDOR") {
    if (!industry) return null;
    return isAuthenticated
      ? ecosystemHeadProfileHref(industry, promo.targetUserId)
      : guestCorridorProfileHref(industry, promo.targetUserId);
  }

  // Expert promos → company profile page (same as Expert listing / "Request to join").
  if (promo.companyId) {
    return isAuthenticated
      ? expertCompanyProfileHref(promo.companyId)
      : guestExpertProfileHref(promo.companyId);
  }

  // Rare fallback when expert has no company record yet.
  if (!industry) return null;
  return expertProfileHref(industry, promo.targetUserId);
}

/**
 * Loads active sector promos and always auto-opens a rotating popup when the sector page mounts.
 */
export default function SectorPromoPopupHost({ sectorSlug }: { sectorSlug: string }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [opened, setOpened] = useState(false);
  const [promo, setPromo] = useState<(SectorPromoModalData & PublicSectorPromo) | null>(null);

  useEffect(() => {
    let cancelled = false;

    setOpened(false);
    setPromo(null);

    trpc.sectorPromo.getForSector
      .query({ sectorSlug })
      .then((res) => {
        if (cancelled) return;
        const promos = (res.promos ?? []) as PublicSectorPromo[];
        if (!promos.length) return;

        const selected = pickRotatedPromo(sectorSlug, promos);
        setPromo({
          ...selected,
          id: selected.id,
          imageUrl: selected.imageUrl,
          ctaLabel: selected.ctaLabel,
        });
        setOpened(true);
      })
      .catch(() => {
        /* non-blocking promo */
      });

    return () => {
      cancelled = true;
    };
  }, [sectorSlug]);

  const handleClose = () => {
    setOpened(false);
  };

  const handleJoin = () => {
    if (!promo) return;
    const href = resolveJoinHref(promo, isAuthenticated);
    setOpened(false);
    if (!href) return;

    const returnTo = `/sector/${sectorSlug}`;
    navigate(href, {
      state:
        promo.targetType === "EXPERT"
          ? { expertUserId: promo.targetUserId, ...expertNavState(returnTo) }
          : expertNavState(returnTo),
    });
  };

  return (
    <SectorPromoModal opened={opened} promo={promo} onClose={handleClose} onJoin={handleJoin} />
  );
}
